<template>
  <div class="challenge-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderPage :page="currentPageInfo" />
    <BlueSection
      v-if="authenticated"
      :connected="authenticated"
      diagonal="top transparent"
      :challenges="userChallenges"
      title="Les challenges de vos communautés"
    />
    <BlueSection
      v-else
      :connected="authenticated"
      diagonal="top transparent"
    />
    <div class="order">
      <section
        v-if="metaContent.value?.description"
        class="section order-last"
      >
        <div
          class="description"
          v-html="metaContent.value?.description"
        />
      </section>
      <div class="order-first">
        <SearchResults :with-search="false">
          <template #title>
            <h2 class="title-strat">
              {{
                !!userChallenges.length
                  ? 'Plus de challenges'
                  : currentPageInfo.title
              }}
            </h2>
          </template>
          <template #results>
            <CardContainer
              :items="challenges"
              no-result-text="Aucun challenge en cours"
            />
          </template>
        </SearchResults>
        <ThePagination
          :total-pages="challengesPages"
          :current-page="challengesCurrentPage"
          :go-to="changePageTo"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLoadChallenges } from '@/composables/useLoadChallenges'

definePageMeta({
  middleware: async (to) => {
    const loadChallenges = useLoadChallenges()
    return loadChallenges(to)
  },
})

const route = useRoute()

const challengesStore = useChallengesStore()
const { challenges, userChallenges, challengesPages, challengesCurrentPage } =
  storeToRefs(challengesStore)
const metaContentStore = useMetaContentStore()

const link = useLinkPrevNext({
  currentPage: challengesCurrentPage,
  pagesCount: challengesPages,
})

const arianeRoute = ref([
  {
    title: 'Tous les challenges',
    route: '',
  },
])

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: 'Tous les challenges PlayStation du mois',
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: 'Tous les challenges We Are PlayStation',
  description:
    'Relevez les challenges du mois de la communauté WAP pour grimper dans le classement.',
  ogTitle: 'Tous les challenges We Are PlayStation',
  ogDescription:
    'Relevez les challenges du mois de la communauté WAP pour grimper dans le classement.',
  ogImage: 'https://www.weareplaystation.fr/img/tous-les-challenges.jpg',
})

const currentPageInfo = {
  title: 'Tous les challenges',
  image: '/img/tous-les-challenges.jpg',
  titleSmall: 'tous les',
  titleBig: 'challenges',
  description:
    'Relevez les challenges du mois de la communauté WAP pour grimper dans le classement.',
}

const { authenticated } = useAuthenticated()

const metaContent = computed(() => {
  return metaContentStore.getTransverseBySlug(route.name)
})

const loadChallenges = useLoadChallenges()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadChallenges(route)
  }
})

const changePageTo = async (index = 1) => {
  if (index > 0) {
    await challengesStore.fetchChallenges({ page: index })
  }
}
</script>

<style lang="scss" scoped>
.challenge-page {
  min-height: 100vh;
  padding-bottom: 10rem;

  .blue-section {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    width: 100vw;
    margin-top: 0;
    padding-right: 0;
    padding-left: 0;

    &.no-connect {
      display: block;
      padding-top: 8rem;
      padding-bottom: 8rem;

      .monthly-challenge {
        width: 100%;
        margin-top: 8rem;
        padding-top: 0;
        padding-bottom: 0;
        padding-left: 8rem;
        background: none;
      }

      &::after {
        z-index: 2;
        border-width: 0 0 5rem 100vw;
        border-color: transparent transparent $blue transparent;
      }

      .diagonal {
        display: none;
      }
    }

    &::after {
      content: '';
      position: absolute;
      top: -5rem;
      right: 0;
      z-index: 2;
      width: 0;
      height: 0;
      border-width: 0 0 5rem 100vw;
      border-style: solid;
      border-color: transparent transparent $blue transparent;
    }

    .join-community {
      padding: 0 2rem 0 8rem;
    }

    .top-waper {
      .title-line {
        .button-more {
          color: $white;

          svg {
            path {
              fill: $white;
            }
          }
        }
      }
    }

    .ranking {
      width: 33%;
      height: 100%;
      padding-top: 4rem;
    }

    .monthly-challenge {
      position: relative;
      width: 67%;
      height: 100%;
      margin-top: 0;
      padding-top: 8.5rem;
      padding-bottom: 9rem;
      padding-left: 12rem;
      background: $challengeBackgroundBlue;

      .diagonal {
        position: absolute;
        top: -5rem;
        left: 0;
        z-index: 3;
        display: inline-block;
        width: 100%;
        height: 5rem;
        overflow: hidden;

        .top-blue {
          position: absolute;
          top: 0;
          left: -33vw;
          z-index: 2;
          width: 0;
          height: 0;
          border-width: 0 0 5rem 100vw;
          border-style: solid;
          border-color: transparent transparent $challengeBackgroundBlue
            transparent;
        }
      }

      .line {
        padding-right: 8rem;

        .title-strat {
          font-family: 'SST Condensed';
          font-size: 2.6rem;
          font-weight: 400;
          line-height: 4.9rem;
          letter-spacing: 0.52rem;
        }
      }

      .slider {
        width: 100%;
        padding-right: 8rem;
      }
    }
  }
}

.order {
  display: flex;
  flex-direction: column;

  &-first {
    order: 1;
  }

  &-last {
    order: 2;
    width: 100%;
  }

  .description {
    &:deep(h2) {
      margin-bottom: 1.5rem;
    }

    &:deep(h3) {
      margin-bottom: 1rem;
      font-family: SST;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 2rem;
      letter-spacing: 0.225rem;
      text-transform: uppercase;
    }

    &:deep(p) {
      margin-bottom: 1rem;
    }

    &:deep(ul) {
      margin-bottom: 1rem;
      padding-left: 1em;
      list-style: disc;
    }
  }
}
</style>
